// File: src/components/OmniCine/Timeline/Clips/PerceptIndicators.tsx
import * as React from "react";

type AnyTransform = {
  id?: string;
  kind?: string;
  clipId?: string;
  startSec?: number;
  endSec?: number;
  atSec?: number;
  breaksConstancy?: boolean;
  severity?: number; // optional
  label?: string; // optional
};

function finiteNum(x: any): x is number {
  return typeof x === "number" && Number.isFinite(x);
}

export function getNowSecFromState(state: any): number {
  // Prefer common names, fall back to 0
  const cands = [
    state?.playheadSec,
    state?.cursorSec,
    state?.nowSec,
    state?.transport?.playheadSec,
    state?.transport?.cursorSec,
  ];
  for (const v of cands) if (finiteNum(v)) return v;
  return 0;
}

export function getActiveBreaksConstancyTransform(state: any, clipId: string, nowSec: number): AnyTransform | null {
  const list: AnyTransform[] =
    state?.perception?.transforms ||
    state?.perception?.constancyBreaks ||
    state?.transforms ||
    [];

  if (!Array.isArray(list) || !clipId) return null;

  // Rule: match clipId and time window (if present)
  const active = list.filter((t) => {
    if (!t) return false;
    if (String(t.clipId || "") !== String(clipId)) return false;

    // Must be a constancy-break if flagged, otherwise accept by kind heuristic
    const isBreak =
      t.breaksConstancy === true ||
      String(t.kind || "").toLowerCase().includes("constancy") ||
      String(t.kind || "").toLowerCase().includes("break");

    if (!isBreak) return false;

    const s = finiteNum(t.startSec) ? t.startSec : finiteNum(t.atSec) ? t.atSec : null;
    const e = finiteNum(t.endSec) ? t.endSec : s;

    if (s == null) return true; // no time info → treat as active
    if (e == null) return Math.abs(nowSec - s) < 0.001;
    return nowSec >= s && nowSec <= e;
  });

  if (!active.length) return null;

  // Prefer highest severity, otherwise latest startSec
  active.sort((a, b) => {
    const sa = finiteNum(a.severity) ? a.severity : 0;
    const sb = finiteNum(b.severity) ? b.severity : 0;
    if (sb !== sa) return sb - sa;

    const aa = finiteNum(a.startSec) ? a.startSec : finiteNum(a.atSec) ? a.atSec : 0;
    const bb = finiteNum(b.startSec) ? b.startSec : finiteNum(b.atSec) ? b.atSec : 0;
    return bb - aa;
  });

  return active[0] ?? null;
}

export function PerceptBadge(props: {
  transform: AnyTransform | null;
  onOpenTransform?: (t: AnyTransform) => void;
  align?: "left" | "right";
}): JSX.Element | null {
  const { transform, onOpenTransform, align = "right" } = props;
  if (!transform) return null;

  const sev = typeof transform.severity === "number" ? transform.severity : 1;
  const label = transform.label || transform.kind || "Percept";
  const dotCls = sev >= 3 ? "bg-rose-400" : sev === 2 ? "bg-amber-300" : "bg-cyan-300";

  return (
    <button
      type="button"
      className={[
        "absolute top-1.5",
        align === "right" ? "right-1.5" : "left-1.5",
        "z-[80]", // above stripe + clip fill
        "pointer-events-auto",
        "rounded-md px-2 py-1",
        "text-[10px] font-semibold tracking-wide",
        "bg-black/55 text-white",
        "border border-white/15",
        "backdrop-blur-md",
        "hover:bg-black/65",
        "focus:outline-none focus-visible:ring-2 ring-white/40 ring-offset-2 ring-offset-black/30",
      ].join(" ")}
      onClick={(e) => {
        e.stopPropagation();
        if (transform && onOpenTransform) onOpenTransform(transform);
      }}
      title="Perception / breaks-constancy transform (click to inspect)"
      aria-label="Perception badge"
    >
      <span className="inline-flex items-center gap-1.5">
        <span className={["inline-block h-2 w-2 rounded-full", dotCls].join(" ")} />
        <span className="opacity-90">{String(label).toUpperCase()}</span>
      </span>
    </button>
  );
}

export function PerceptStripe(props: {
  transform: AnyTransform | null;
}): JSX.Element | null {
  const { transform } = props;
  if (!transform) return null;

  const sev = typeof transform.severity === "number" ? transform.severity : 1;

  // Keep stripe subtle but visible. No pointer capture.
  const stripeOpacity = sev >= 3 ? "opacity-60" : sev === 2 ? "opacity-45" : "opacity-35";

  return (
    <div
      className={[
        "absolute inset-0",
        "z-[60]", // below badge, above fill
        "pointer-events-none",
      ].join(" ")}
      aria-hidden="true"
    >
      <div
        className={[
          "absolute inset-0",
          stripeOpacity,
          // diagonal hazard stripe
          "bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.0)_0px,rgba(255,255,255,0.0)_8px,rgba(255,255,255,0.35)_8px,rgba(255,255,255,0.35)_12px)]",
        ].join(" ")}
      />
      <div className="absolute inset-0 bg-black/10" />
    </div>
  );
}
